import React, { useState, useEffect, useRef } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AuthShell, GoPalLogo } from './GoPalLogo';

const EyeOpen = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeClosed = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export function LoginScreen({ supabase }: { supabase: SupabaseClient }) {
  // ── Sign-in state ─────────────────────────────────────────
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  // ── Reset-password state ──────────────────────────────────
  const [showReset,    setShowReset]    = useState(false);
  const [resetEmail,   setResetEmail]   = useState('');
  const [resetSent,    setResetSent]    = useState(false);
  const [resetError,   setResetError]   = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => { emailRef.current?.focus(); }, []);

  // ── Handlers ──────────────────────────────────────────────
  async function handleSignIn() {
    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }
    setError('');
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (err) {
      const msg = err.message?.toLowerCase() || '';
      if (msg.includes('invalid login credentials') || msg.includes('invalid_grant')) {
        setError('Wrong email or password. Please try again.');
      } else if (msg.includes('email not confirmed')) {
        setError('Please confirm your email address before signing in.');
      } else {
        setError(err.message || 'Could not sign in. Please try again.');
      }
      setLoading(false);
      return;
    }
    // Success — App.tsx will detect the session via onAuthStateChange and route to dashboard.
    setLoading(false);
  }

  async function handleResetSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!resetEmail.trim()) { setResetError('Please enter your email'); return; }
    setResetError(null);
    setResetLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
      redirectTo: 'https://orders.getgopal.co.nz',
    });
    if (err) {
      setResetError('Could not send reset email. Please try again.');
      setResetLoading(false);
    } else {
      setResetSent(true);
      setResetLoading(false);
    }
  }

  function backToLogin() {
    setShowReset(false);
    setResetSent(false);
    setResetEmail('');
    setResetError(null);
  }

  // ── Reset-password view ───────────────────────────────────
  if (showReset) {
    return (
      <AuthShell>
        <div className="text-center mb-5">
          <GoPalLogo />
          <h1 className="text-xl font-bold" style={{ letterSpacing: '-.02em', color: 'var(--text)', marginBottom: 4 }}>
            Reset your password
          </h1>
          <p className="text-sm text-subtle">Enter your email and we'll send you a reset link.</p>
        </div>

        {resetSent ? (
          <>
            <div
              className="text-sm mb-4"
              style={{
                padding: 'var(--sp-3)', background: 'var(--surface2)',
                borderRadius: 'var(--r)', textAlign: 'center', color: 'var(--text)',
              }}
            >
              If an account exists for <strong>{resetEmail}</strong>, you'll receive a reset link shortly.
            </div>
            <button
              type="button"
              onClick={backToLogin}
              className="btn btn-ghost btn-sm w-full"
            >
              Back to login
            </button>
          </>
        ) : (
          <form onSubmit={handleResetSubmit}>
            {resetError && (
              <div
                className="text-xs mb-3"
                style={{
                  color: 'var(--red)', background: 'var(--red-bg)',
                  padding: 'var(--sp-2) var(--sp-3)', borderRadius: 'var(--r)',
                }}
              >{resetError}</div>
            )}
            <div className="mb-3">
              <label className="field-label">Email address</label>
              <input
                className="input"
                type="email"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                placeholder="your@email.com"
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-lg w-full"
              disabled={resetLoading}
              style={{ marginBottom: 8 }}
            >
              {resetLoading ? 'Sending…' : 'Send reset link'}
            </button>
            <button
              type="button"
              onClick={backToLogin}
              className="btn btn-ghost btn-sm w-full"
            >
              Back to login
            </button>
          </form>
        )}
      </AuthShell>
    );
  }

  // ── Sign-in view ──────────────────────────────────────────
  return (
    <AuthShell>
      <div className="text-center mb-5">
        <GoPalLogo />
        <h1 className="text-xl font-bold" style={{ letterSpacing: '-.02em', color: 'var(--text)', marginBottom: 4 }}>
          GoPal
        </h1>
        <p className="text-sm text-subtle">Operations Procurement Manager</p>
      </div>

      {error && (
        <div
          className="text-xs mb-3"
          style={{
            color: 'var(--red)', background: 'var(--red-bg)',
            padding: 'var(--sp-2) var(--sp-3)', borderRadius: 'var(--r)',
          }}
        >{error}</div>
      )}

      <div className="mb-3">
        <label className="field-label">Email address</label>
        <input
          ref={emailRef}
          className="input"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSignIn(); }}
          placeholder="your@email.com"
          autoComplete="email"
        />
      </div>

      <div className="mb-4">
        <label className="field-label">Password</label>
        <div className="relative">
          <input
            className="input"
            type={showPwd ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSignIn(); }}
            placeholder="Your password"
            autoComplete="current-password"
            style={{ paddingRight: 40 }}
          />
          <button
            type="button"
            onClick={() => setShowPwd(v => !v)}
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 2, color: 'var(--text3)',
            }}
          >{showPwd ? <EyeClosed /> : <EyeOpen />}</button>
        </div>
      </div>

      <button
        className="btn btn-primary btn-lg w-full"
        disabled={loading}
        onClick={handleSignIn}
        style={{ marginBottom: 8 }}
      >
        {loading ? 'Signing in…' : 'Sign in'}
      </button>

      <button
        type="button"
        onClick={() => setShowReset(true)}
        className="btn btn-ghost btn-sm w-full"
      >
        Forgot your password?
      </button>
    </AuthShell>
  );
}
